import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  generateQuoteIndicationDraft,
  quoteIndicationPreflight,
} from "../../../api/routes";
import { SafeReportResponse } from "../Conversion";
import styles from "./QuoteIndicationModal.module.css";
import { InputNumber } from "primereact/inputnumber";
import { Skeleton } from "primereact/skeleton";
import { useToast } from "../../../context-providers/Toast";
import { Dropdown } from "primereact/dropdown";
import { readableLayer, sortedLayersFromReport } from "../layers/util";
import { formatExactMoneyDollars, LayerPremiumSummary } from "../Money";
import { useCalculatedPremium } from "../data";
import {
  getEffectiveIncludedPerils,
  perilSentenceFromEnabledPerils,
} from "../general-input/util";

type QuoteIndicationModalProps = {
  specialtyPropertyInfo: SafeReportResponse;
  visible: boolean;
  setVisible: (visible: boolean) => void;
};

export const QuoteIndicationModal: React.FC<QuoteIndicationModalProps> = ({
  specialtyPropertyInfo,
  visible,
  setVisible,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [premiumLow, setPremiumLow] = useState<number | null>(null);
  const [premiumHigh, setPremiumHigh] = useState<number | null>(null);
  const sortedLayers = sortedLayersFromReport(specialtyPropertyInfo);
  const layerOptions = sortedLayers.map((l) => ({
    label: `${readableLayer(l)} (${l.layer_source})`,
    value: l.layer_id!,
  }));
  const [selectedLayerId, setSelectedLayerId] = useState(
    sortedLayers.find((layer) => layer.is_enabled)?.layer_id
  );
  const selectedLayer = sortedLayers.find(
    (layer) => layer.layer_id === selectedLayerId
  );

  const reportId = specialtyPropertyInfo.report_id;
  const { data: premiumData } = useCalculatedPremium(reportId, visible);

  const selectedLayerTotals = useMemo(() => {
    if (!premiumData || !selectedLayer?.layer_id) return null;
    const layerPremium = premiumData.layers.find(
      (l) => l.layer_id === selectedLayer.layer_id
    );
    if (!layerPremium) return null;
    return layerPremium.totals["resolved"] ?? null;
  }, [premiumData, selectedLayer]);

  const effectivePerils = useMemo(() => {
    const allPerils =
      specialtyPropertyInfo.report_json.policy_info?.included_perils ?? [];
    if (!selectedLayer?.layer_id) return allPerils;
    return getEffectiveIncludedPerils(
      allPerils,
      premiumData,
      selectedLayer.layer_id
    );
  }, [specialtyPropertyInfo, selectedLayer, premiumData]);

  const perilsInsured = useMemo(
    () =>
      perilSentenceFromEnabledPerils(
        effectivePerils,
        specialtyPropertyInfo.report_json.policy_info?.peril_sentence_override
      ),
    [effectivePerils, specialtyPropertyInfo]
  );

  const { data: preflight } = useQuery({
    queryKey: [
      "quoteIndicationPreflight",
      reportId,
      selectedLayerId,
      effectivePerils,
      perilsInsured,
    ],
    queryFn: () =>
      quoteIndicationPreflight(
        reportId,
        selectedLayerId!,
        effectivePerils,
        perilsInsured
      ),
    enabled: visible && !!selectedLayerId && !!premiumData,
  });
  const isPreflightLoading = !!selectedLayerId && !preflight;

  const renderTextOrSkeleton = (value: string | undefined, width = "70%") =>
    isPreflightLoading ? (
      <Skeleton width={width} height="1.2em" />
    ) : (
      (value ?? "")
    );

  const premiumRange =
    premiumLow != null && premiumHigh != null
      ? `${formatExactMoneyDollars(premiumLow, { withDollarSign: true, withRounding: true })} - ${formatExactMoneyDollars(premiumHigh, { withDollarSign: true, withRounding: true })}`
      : "";

  const toast = useToast();

  const handleCreateDraft = async () => {
    if (!selectedLayerId) return;
    setIsLoading(true);

    try {
      const response = await generateQuoteIndicationDraft(
        reportId,
        selectedLayerId,
        effectivePerils,
        perilsInsured,
        premiumRange
      );

      window.open(response.draft_url, "_blank");
    } catch (error) {
      toast.show({
        severity: "error",
        summary: "Error Generating Draft Email",
        detail: "Something failed while generating the draft email.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      header="Quote Indication"
      visible={visible}
      style={{
        width: "min(700px, calc(100vw - 24px))",
      }}
      onHide={() => setVisible(false)}
      footer={
        <Button
          loading={isLoading}
          label="Create Draft"
          icon="pi pi-envelope"
          onClick={handleCreateDraft}
          disabled={!selectedLayer || !preflight || isPreflightLoading}
        />
      }
    >
      <div className={styles.verticalContainer}>
        <div className={styles.verticalContainerSmall}>
          <span className={styles.header}>Layer</span>
          <Dropdown
            value={selectedLayerId}
            options={layerOptions}
            onChange={(e) => setSelectedLayerId(e.value)}
            placeholder="Select a layer"
            style={{ width: "100%", maxWidth: "300px" }}
          />
        </div>
        <div className={styles.verticalContainerSmall}>
          <span className={styles.header}>Account Name</span>
          <span>{renderTextOrSkeleton(preflight?.account_name, "50%")}</span>
        </div>
        <div className={styles.verticalContainerSmall}>
          <span className={styles.header}>Effective Date</span>
          <span>
            {renderTextOrSkeleton(
              preflight &&
                `${preflight.effective_date} - ${preflight.expiration_date}`,
              "40%"
            )}
          </span>
        </div>
        <div className={styles.verticalContainerSmall}>
          <span className={styles.header}>Perils Insured</span>
          <span>{renderTextOrSkeleton(perilsInsured, "80%")}</span>
        </div>
        <div className={styles.verticalContainerSmall}>
          <span className={styles.header}>Limit of Liability</span>
          <span>
            {renderTextOrSkeleton(preflight?.limit_of_liability, "60%")}
          </span>
        </div>
        <div className={styles.verticalContainerSmall}>
          <span className={styles.header}>Total Insured Value</span>
          <span>
            {renderTextOrSkeleton(preflight?.total_insured_value, "30%")}
          </span>
        </div>
        <div className={styles.verticalContainerSmall}>
          <span className={styles.header}>Deductibles</span>
          <div className={styles.verticalContainerSmall}>
            {isPreflightLoading ? (
              <>
                <Skeleton width="85%" height="1.2em" />
                <Skeleton width="75%" height="1.2em" />
                <Skeleton width="80%" height="1.2em" />
              </>
            ) : (
              (preflight?.deductibles ?? []).map((entry, i) => (
                <div key={i}>
                  <b style={{ fontSize: "14px" }}>{entry.name}:</b>{" "}
                  {entry.description}
                </div>
              ))
            )}
          </div>
        </div>
        <div className={styles.verticalContainerSmall}>
          <span className={styles.header}>Commission</span>
          <span>{renderTextOrSkeleton(preflight?.commission, "20%")}</span>
        </div>
        <div className={styles.verticalContainerSmall}>
          <span className={styles.header}>Premium Range (Gross Layer)</span>
          {selectedLayerTotals && (
            <LayerPremiumSummary
              layerTotal={selectedLayerTotals.layer_total}
              shareTotal={selectedLayerTotals.share_total}
            />
          )}
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <InputNumber
              value={premiumLow}
              onValueChange={(e) => setPremiumLow(e.value ?? null)}
              mode="currency"
              currency="USD"
              locale="en-US"
              placeholder="Low"
              style={{ flex: 1, minWidth: 0 }}
              inputStyle={{ width: "100%" }}
            />
            <span>–</span>
            <InputNumber
              value={premiumHigh}
              onValueChange={(e) => setPremiumHigh(e.value ?? null)}
              mode="currency"
              currency="USD"
              locale="en-US"
              placeholder="High"
              style={{ flex: 1, minWidth: 0 }}
              inputStyle={{ width: "100%" }}
            />
          </div>
        </div>
      </div>
    </Dialog>
  );
};
